import { AfterViewChecked, ChangeDetectorRef, Component, inject, OnDestroy, ChangeDetectionStrategy } from '@angular/core';

import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { isEmpty, isNil, omit, omitBy, some } from 'lodash-es';
import { finalize, Subject, takeUntil } from 'rxjs';
import { Store } from '@ngrx/store';
import { ActiveModal } from '@rocketfinancialcorp/rocket-ui/modal';
import { RktFormComponent } from '@rocketfinancialcorp/rocket-ui/form';
import { RktIconComponent } from '@rocketfinancialcorp/rocket-ui/icon';
import { RktButtonDirective } from '@rocketfinancialcorp/rocket-ui/button';
import { authFeature } from '@store';
import { MESSAGE } from '@shared/constants';
import {
  Address,
  Branding,
  BusinessAccount,
  BusinessAccountAddressesData,
  BusinessAccountContact,
  BusinessAccountFormData,
  CreateBusinessAccountParams,
  FormModel,
  UpdateBusinessAccountFormData,
} from '@shared/models';
import { BusinessAccountService, NotificationService } from '@shared/services';
import { ErrorUtils, isTenantBusinessAccount, phoneNumberFormat } from '@shared/utils';
import { ExpandableFormSectionComponent, AddressEditComponent } from '@shared/components';
import { businessAccountContactPersonFields, updateBusinessAccountFields } from '../business-account-fields';

@Component({
  templateUrl: './update-business-account.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    RktIconComponent,
    RktFormComponent,
    RktButtonDirective,
    FormsModule,
    ReactiveFormsModule,
    AddressEditComponent,
    ExpandableFormSectionComponent,
  ],
})
export class UpdateBusinessAccountComponent implements AfterViewChecked, OnDestroy {
  businessAccountService = inject(BusinessAccountService);

  notificationService = inject(NotificationService);

  ref = inject(ChangeDetectorRef);

  activeModal = inject(ActiveModal);

  store = inject(Store);

  businessAccountId!: BusinessAccount['id'];

  submitError = '';

  editForm = new FormGroup({});

  formModel: FormModel<UpdateBusinessAccountFormData> = {};

  industries$ = this.store.select(authFeature.selectIndustries);

  formFields = updateBusinessAccountFields(this.industries$);

  contactPersonFormFields = businessAccountContactPersonFields;

  get isSomeAddressFormInvalid(): boolean {
    return some(this.addressFormValidity, (isValid: boolean) => !isValid);
  }

  get isSomeSectionExpanded(): boolean {
    return some(this.sectionsExpanded, (isExpanded: boolean) => isExpanded);
  }

  isLoading = false;

  sectionsExpanded: Record<string, boolean> = {
    profileDetail: false,
    primaryContactPerson: false,
    physicalAddresses: false,
    mailingAddresses: false,
    shippingAddresses: false,
  };

  addressFormValidity: Record<string, boolean> = {
    PHYSICAL: false,
    MAILING: false,
    SHIPPING: false,
  };

  businessAccountAddresses: BusinessAccountAddressesData = {};

  /**
   * The branding object as stored on the Business Account. The backend replaces `branding` wholesale on update, so
   * the fields this form does not expose have to be echoed back verbatim to avoid clearing them.
   */
  businessAccountBranding?: Branding;

  /** Tenant root accounts own the Linx base URL; child accounts inherit it and must not override it. */
  isTenant = false;

  private destroy$ = new Subject<void>();

  ngAfterViewChecked() {
    this.ref.detectChanges();
  }

  ngOnDestroy(): void {
    this.editForm.markAsUntouched();
    this.destroy$.next();
    this.destroy$.complete();
  }

  modalInitData({
    businessAccountId,
    businessAccountFormData,
    businessAccountAddressesData,
    businessAccountPrimaryContactData,
    businessAccountBrandingData,
    businessAccountParentId,
  }: {
    businessAccountId: BusinessAccount['id'];
    businessAccountFormData: BusinessAccountFormData;
    businessAccountAddressesData: BusinessAccountAddressesData;
    businessAccountPrimaryContactData: BusinessAccountContact;
    businessAccountBrandingData?: Branding;
    businessAccountParentId?: string;
  }): void {
    this.businessAccountId = businessAccountId;

    this.businessAccountBranding = businessAccountBrandingData;

    this.isTenant = isTenantBusinessAccount(businessAccountParentId);

    this.formModel = {
      ...this.formModel,
      ...businessAccountFormData,
      isTenantBusinessAccount: this.isTenant,
      linxBaseUrl: this.isTenant ? businessAccountBrandingData?.linxBaseUrl : undefined,
      primaryContactFirstName: businessAccountPrimaryContactData?.firstName,
      primaryContactLastName: businessAccountPrimaryContactData?.lastName,
      primaryContactMiddleName: businessAccountPrimaryContactData?.middleName,
      primaryContactPhoneNumber: businessAccountPrimaryContactData?.primaryPhoneNumber?.number,
      primaryContactEmailAddress: businessAccountPrimaryContactData?.primaryEmail?.value,
    };

    this.businessAccountAddresses = businessAccountAddressesData;

    setTimeout(() => {
      this.editForm.markAllAsTouched();
    }, 0);
  }

  setAddresses(addresses: Address[], addressType: keyof BusinessAccountAddressesData): void {
    this.businessAccountAddresses[addressType] = addresses;
  }

  toggleSection(section: string) {
    this.sectionsExpanded[section] = !this.sectionsExpanded[section];
  }

  toggleAll(state: boolean) {
    Object.keys(this.sectionsExpanded).map((sectionKey) => (this.sectionsExpanded[sectionKey] = state));
  }

  onSave() {
    this.editForm.markAllAsTouched();

    if (this.editForm.invalid || this.isSomeAddressFormInvalid) {
      return;
    }

    if (this.isSomeSectionExpanded) {
      this.toggleAll(false);
    }

    this.submitError = '';

    this.isLoading = true;

    const {
      primaryPhoneNumber,
      primaryEmail,
      legalId,
      linxBaseUrl,
      primaryContactFirstName,
      primaryContactLastName,
      primaryContactMiddleName,
      primaryContactEmailAddress,
      primaryContactPhoneNumber,
      ...rest
    } = this.formModel;
    const { physicalAddresses = [], mailingAddresses = [], shippingAddresses = [] } = this.businessAccountAddresses;
    const businessAccountPrimaryContact = omitBy(
      {
        firstName: primaryContactFirstName!,
        lastName: primaryContactLastName!,
        middleName: isEmpty(primaryContactMiddleName) ? undefined : primaryContactMiddleName,
        primaryEmail: isEmpty(primaryContactEmailAddress)
          ? undefined
          : {
              value: primaryContactEmailAddress!,
            },
        primaryPhoneNumber: isEmpty(primaryContactPhoneNumber)
          ? undefined
          : {
              number: phoneNumberFormat(primaryContactPhoneNumber)!,
            },
      },
      isEmpty,
    );

    this.businessAccountService
      .updateBusinessAccount({
        businessAccountId: this.businessAccountId,
        data: {
          /* `isTenantBusinessAccount` only drives field visibility in the form and is not part of the API contract */
          ...omit(rest, ['isTenantBusinessAccount']),
          phoneNumbers: [{ number: phoneNumberFormat(primaryPhoneNumber), primary: true }],
          emails: [{ value: primaryEmail!, primary: true, verified: false }],
          legalId: rest.legalIdCountryCode === 'USA' ? legalId?.replace(/(\d{2})(\d{7})/, '$1-$2') : legalId,
          addresses: [...physicalAddresses, ...mailingAddresses, ...shippingAddresses],
          contacts: isEmpty(businessAccountPrimaryContact) ? undefined : [businessAccountPrimaryContact],
          branding: this.buildBranding(linxBaseUrl),
        },
      })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isLoading = false)),
      )
      .subscribe({
        next: () => {
          this.notificationService.displaySuccess('Business Account updated successfully.');
          this.activeModal.close(true);
        },
        error: (error) => this.handleError(error),
      });
  }

  modelChange(data: unknown) {
    const { legalId } = data as CreateBusinessAccountParams;
    if (legalId) {
      this.formModel = {
        ...this.formModel,
        legalId,
      };
    }
  }

  /**
   * Merges the edited Linx base URL over the stored branding object. The backend treats a non-null `branding` as a
   * full replacement, so every other branding field has to travel with it. Returning `undefined` omits `branding`
   * from the request, which tells the backend to keep whatever it has.
   *
   * Child Business Accounts never send `branding` at all — this form exposes no branding field for them, so there is
   * nothing to merge and replacing the stored object wholesale could only lose data.
   */
  private buildBranding(linxBaseUrl?: string): Branding | undefined {
    if (!this.isTenant) {
      return undefined;
    }

    const branding = omitBy({ ...this.businessAccountBranding, linxBaseUrl: linxBaseUrl?.trim() || undefined }, isNil) as Branding;

    if (isEmpty(branding) && isEmpty(this.businessAccountBranding)) {
      return undefined;
    }

    return branding;
  }

  private handleError(error?: string | number): void {
    if (typeof error === 'string') {
      this.submitError = error;
    } else {
      this.submitError = error === 403 ? MESSAGE.PERMISSION_DENIED : MESSAGE.UNKNOWN_ERROR;
    }

    ErrorUtils.catchError('businessAccountService.updateBusinessAccount', error);
  }
}
